import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    MagnifyingGlass, Package, Warning, ClockCounterClockwise, ArrowsCounterClockwise,
} from "@phosphor-icons/react";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";

const STATUS_TONE = {
    active:    "bg-emerald-100 text-emerald-800 border-emerald-200",
    exhausted: "bg-slate-100 text-slate-700 border-slate-200",
    recalled:  "bg-rose-100 text-rose-800 border-rose-200",
    expired:   "bg-amber-100 text-amber-800 border-amber-200",
};

function daysToExpiry(iso) {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.floor(ms / 86400000);
}

export function BatchesTab() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [materialFilter, setMaterialFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [materials, setMaterials] = useState([]);
    const [detailBatch, setDetailBatch] = useState(null);
    const [recallFor, setRecallFor] = useState(null);

    // Load all batches (no server-side status filter) so KPI chip counts stay accurate as user flips chip
    const load = async () => {
        const p = new URLSearchParams();
        if (materialFilter) p.set("material_id", materialFilter);
        const [b, m] = await Promise.all([
            api.get(`/admin/inventory/batches${p.toString() ? `?${p}` : ""}`).then((r) => r.data),
            api.get("/admin/inventory/materials").then((r) => r.data),
        ]);
        setItems(b || []);
        setMaterials(m || []);
    };
    useEffect(() => { load(); }, [materialFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const statusCounts = useMemo(() => {
        const c = { all: items.length, active: 0, exhausted: 0, recalled: 0, expired: 0 };
        items.forEach((b) => { if (c[b.status] !== undefined) c[b.status] += 1; });
        return c;
    }, [items]);

    const filtered = useMemo(() => {
        const ql = q.trim().toLowerCase();
        return items.filter((b) => {
            if (statusFilter && b.status !== statusFilter) return false;
            if (!ql) return true;
            return (b.lot_number || "").toLowerCase().includes(ql)
                || (b.batch_code || "").toLowerCase().includes(ql)
                || (b.material_sku || "").toLowerCase().includes(ql)
                || (b.material_name || "").toLowerCase().includes(ql)
                || (b.supplier || "").toLowerCase().includes(ql);
        });
    }, [items, q, statusFilter]);

    return (
        <div className="space-y-4" data-testid="batches-tab">
            <FilterSearchBar
                query={q}
                setQuery={setQ}
                placeholder="Search by lot, batch code, SKU, supplier…"
                searchTestid="batches-search"
                testid="batches-search-bar"
                rightSlot={(
                    <select
                        className="h-9 rounded-md border border-input bg-card px-3 text-xs font-medium"
                        value={materialFilter}
                        onChange={(e) => setMaterialFilter(e.target.value)}
                        data-testid="batches-material-filter"
                    >
                        <option value="">All materials</option>
                        {materials.map((m) => (
                            <option key={m.id} value={m.id}>{m.sku} — {m.name}</option>
                        ))}
                    </select>
                )}
            />

            <FilterChipRow
                testid="batches-chip-row"
                chipTestidPrefix="batches-status"
                value={statusFilter || "all"}
                onChange={(v) => setStatusFilter(v === "all" ? "" : v)}
                chips={[
                    { id: "all",       label: "All",       count: statusCounts.all,       tone: "primary" },
                    { id: "active",    label: "Active",    count: statusCounts.active,    tone: "emerald" },
                    { id: "recalled",  label: "Recalled",  count: statusCounts.recalled,  tone: "rose" },
                    { id: "expired",   label: "Expired",   count: statusCounts.expired,   tone: "amber" },
                    { id: "exhausted", label: "Exhausted", count: statusCounts.exhausted, tone: "slate" },
                ]}
            />

            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                    <div className="col-span-3">Material / Lot</div>
                    <div className="col-span-2">Supplier</div>
                    <div className="col-span-2">Received / Expires</div>
                    <div className="col-span-2 text-right">Remaining</div>
                    <div className="col-span-1">Status</div>
                    <div className="col-span-2 text-right">Actions</div>
                </div>
                {filtered.length === 0 ? (
                    <div className="py-10 text-center text-muted-foreground text-sm" data-testid="batches-empty">
                        {items.length === 0
                            ? "No batches recorded yet — receive your first lot from the Materials tab."
                            : "No batches match the search."}
                    </div>
                ) : filtered.map((b) => {
                    const dte = daysToExpiry(b.expiry_date);
                    const expSoon = dte !== null && dte < 90 && b.status === "active";
                    return (
                        <div
                            key={b.id}
                            className={`grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3 border-b border-border last:border-0 items-center ${b.status === "recalled" ? "bg-rose-50/30" : ""}`}
                            data-testid={`batch-row-${b.id}`}
                        >
                            <div className="col-span-3 min-w-0">
                                <div className="font-mono text-[11px] font-bold uppercase text-muted-foreground">{b.batch_code}</div>
                                <div className="font-semibold text-sm truncate">{b.material_sku} <span className="text-muted-foreground text-xs font-normal">· Lot {b.lot_number}</span></div>
                                <div className="text-[11px] text-muted-foreground truncate">{b.material_name}</div>
                            </div>
                            <div className="col-span-2 text-sm truncate text-muted-foreground">{b.supplier || "—"}</div>
                            <div className="col-span-2 text-xs">
                                <div>R: <span className="font-medium">{b.received_date || "—"}</span></div>
                                <div className="flex items-center gap-1">
                                    E: <span className={`font-medium ${expSoon ? "text-amber-700" : ""}`}>{b.expiry_date || "—"}</span>
                                    {expSoon && <Warning size={11} weight="fill" className="text-amber-600" />}
                                </div>
                            </div>
                            <div className="col-span-2 text-right">
                                <div className="font-bold tabular-nums">{Number(b.remaining_qty || 0).toLocaleString()}<span className="text-muted-foreground text-xs ml-1">/ {Number(b.initial_qty || 0).toLocaleString()}</span></div>
                            </div>
                            <div className="col-span-1">
                                <span className={`inline-block text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border ${STATUS_TONE[b.status] || STATUS_TONE.active}`}>
                                    {b.status}
                                </span>
                            </div>
                            <div className="col-span-2 text-right flex items-center justify-end gap-1">
                                <button
                                    type="button"
                                    onClick={() => setDetailBatch(b)}
                                    title="View usage"
                                    className="p-1 hover:bg-secondary rounded"
                                    data-testid={`batch-detail-${b.id}`}
                                >
                                    <ClockCounterClockwise size={14} className="text-primary" />
                                </button>
                                {b.status === "active" && (
                                    <button
                                        type="button"
                                        onClick={() => setRecallFor(b)}
                                        title="Recall this lot"
                                        className="p-1 hover:bg-rose-100 rounded"
                                        data-testid={`batch-recall-${b.id}`}
                                    >
                                        <ArrowsCounterClockwise size={14} className="text-rose-600" />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <BatchDetailDialog batchId={detailBatch?.id} open={!!detailBatch} onClose={() => setDetailBatch(null)} />
            <RecallDialog batch={recallFor} onClose={() => setRecallFor(null)} onRecalled={() => { setRecallFor(null); load(); }} />
        </div>
    );
}

function BatchDetailDialog({ batchId, open, onClose }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        if (open && batchId) {
            setData(null);
            api.get(`/admin/inventory/batches/${batchId}`).then((r) => setData(r.data));
        }
    }, [open, batchId]);
    if (!open) return null;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="batch-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Package size={20} weight="fill" className="text-primary" />
                        Batch trace — {data?.batch_code || "…"}
                    </DialogTitle>
                    <DialogDescription>
                        Every case this batch was consumed on.
                    </DialogDescription>
                </DialogHeader>
                {!data ? (
                    <div className="py-6 text-center text-muted-foreground text-sm">Loading…</div>
                ) : (
                    <div className="space-y-3">
                        <div className="rounded-xl bg-muted/30 border border-border p-3 text-sm space-y-1">
                            <div><span className="text-muted-foreground">Material:</span> <span className="font-semibold">{data.material_sku} — {data.material_name}</span></div>
                            <div><span className="text-muted-foreground">Lot:</span> <span className="font-mono">{data.lot_number}</span></div>
                            <div><span className="text-muted-foreground">Supplier:</span> {data.supplier || "—"}</div>
                            <div><span className="text-muted-foreground">Received:</span> {data.received_date || "—"} · <span className="text-muted-foreground">Expires:</span> {data.expiry_date || "—"}</div>
                            <div><span className="text-muted-foreground">Remaining:</span> <span className="font-bold tabular-nums">{data.remaining_qty} / {data.initial_qty}</span></div>
                            {data.status === "recalled" && (
                                <div className="text-rose-800 font-semibold mt-1">⚠ Recalled: {data.recall_reason}</div>
                            )}
                        </div>
                        <div className="font-display font-bold text-sm mt-3">Consumption history ({data.consumption?.length || 0})</div>
                        {(data.consumption || []).length === 0 ? (
                            <div className="text-sm text-muted-foreground italic">Not yet consumed on any case.</div>
                        ) : (
                            <div className="rounded-xl border border-border overflow-hidden">
                                {data.consumption.map((c, i) => (
                                    <div key={i} className="px-3 py-2 border-b border-border last:border-0 grid grid-cols-1 md:grid-cols-12 gap-2 text-sm" data-testid={`batch-consumption-${i}`}>
                                        <div className="md:col-span-3 font-mono text-xs font-semibold">{c.case_code || "—"}</div>
                                        <div className="md:col-span-3 truncate">{c.patient_name || "—"}</div>
                                        <div className="md:col-span-3 truncate text-muted-foreground">{c.doctor_name || "—"}</div>
                                        <div className="md:col-span-2 text-right text-xs">{c.created_at?.slice(0, 10)}</div>
                                        <div className="md:col-span-1 text-right font-bold tabular-nums">−{Math.abs(c.delta || 0)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function RecallDialog({ batch, onClose, onRecalled }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (batch) setReason(""); }, [batch]);
    if (!batch) return null;
    const submit = async () => {
        if (reason.trim().length < 5) { toast.error("Reason must be at least 5 characters"); return; }
        setBusy(true);
        try {
            const r = await api.post(`/admin/inventory/batches/${batch.id}/recall`, { reason: reason.trim() });
            toast.success(`Batch recalled — ${r.data.affected_case_count} case(s) flagged`);
            onRecalled?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to recall");
        } finally { setBusy(false); }
    };
    return (
        <Dialog open={!!batch} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent data-testid="batch-recall-dialog">
                <DialogHeader>
                    <DialogTitle className="text-rose-800 flex items-center gap-2">
                        <ArrowsCounterClockwise size={20} weight="fill" /> Recall batch {batch.batch_code}
                    </DialogTitle>
                    <DialogDescription>
                        Marks <span className="font-semibold">Lot {batch.lot_number}</span> as recalled and lists every case that used it. Stock is NOT auto-decremented — handle that with a separate adjustment.
                    </DialogDescription>
                </DialogHeader>
                <div>
                    <Label>Recall reason<span className="text-rose-600">*</span></Label>
                    <Textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Supplier safety notice ref. SN-2026-04. Sheets to be quarantined."
                        rows={3}
                        data-testid="batch-recall-reason"
                    />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="batch-recall-confirm">
                        {busy ? "Recalling…" : "Recall batch"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default BatchesTab;
